import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const Lightbox = ({ images, index, onClose, onNav }) => {
  const open = index !== null && index >= 0;

  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    },
    [open, onClose, onNav]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  const img = open ? images[index] : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 px-4 py-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          onClick={onClose}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors duration-300 hover:text-white md:right-8 md:top-8"
            data-testid="lightbox-close"
          >
            <X size={18} strokeWidth={1.4} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); onNav(-1); }}
            aria-label="Previous"
            className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors duration-300 hover:text-white md:left-8"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={20} strokeWidth={1.4} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNav(1); }}
            aria-label="Next"
            className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors duration-300 hover:text-white md:right-8"
            data-testid="lightbox-next"
          >
            <ChevronRight size={20} strokeWidth={1.4} />
          </button>

          <AnimatePresence mode="wait">
            <motion.img
              key={img.src}
              src={img.src}
              alt={img.alt || ""}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[82vh] max-w-[92vw] object-contain shadow-2xl"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.45, ease: EASE }}
            />
          </AnimatePresence>

          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 font-sans text-[11px] tracking-[0.3em] text-white/50">
            {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
